import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { DistrictCount } from 'src/app/models/DistrictCount';
import { ElectionService } from 'src/app/services/election.service';
import { ExpandedMapDialogComponent } from '../expanded-map-dialog/expanded-map-dialog.component';
import { SubTitleService } from 'src/app/services/subTitleService';
import { IElectionDate } from 'src/app/interfaces/IElectionDate';
import { IReportCountProgress } from 'src/app/interfaces/IReportCountProgress';

@Component({
  selector: 'app-page-ha-district',
  templateUrl: './page-ha-district.component.html',
  styleUrls: ['./page-ha-district.component.scss']
})
export class PageHaDistrictComponent implements AfterViewInit, OnDestroy {

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();
  public district: DistrictCount | null = null;


  public rowHeight = 90;
  public bodyHeight: string = '' + (window.innerHeight - 110) + 'px';
  public mobile = false;

  public disclaimer = '';
  public finality = '';
  public lastUpdate: Date = new Date();
  public year = '';
  public twoCandidateTitle = '';
  public isFinal: boolean = false;
  public electionData: IElectionDate | null = null;

  constructor(
    public dialog: MatDialog,
    public electionService: ElectionService,
    public subTitleService: SubTitleService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.updateDistrict(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.updateDistrict(this.electionService.selectedPage);
    });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }


  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.updateDistrict(this.electionService.selectedPage);
    }
  }
  


  private updateDistrict(page: ISelectPage): void {
    this.lastUpdate = this.electionService.getLastUpdate(page.electionDateString ?? '', true);
    this.electionService.updateSubTitle(page.electionDateString ?? '');
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');

    // Set isFinal flag
    this.isFinal = election?.haChange?.electionStatus === ElectionStatusEnum.final;

    this.district = election?.electionCount?.getDistrict(this.electionService.selectedPage.countDistrict) ?? null;

    if (election?.haChange?.electionStatus === ElectionStatusEnum.final) {
      this.twoCandidateTitle = 'Two Candidate Preferred Result';
    } else {
      this.twoCandidateTitle = `Candidates currently leading in ${this.district?.districtName}`;
    }
    this.changeDetector.detectChanges();
    this.finality = 'Provisional';
    this.disclaimer = 'Please note, results are provisional. Results will be published progressively. The figures below may not include all votes.';
   // this.year = this.electionService.dataStore.electionCount?.electionDate.getFullYear.toString() ?? '';
   
  }

  public openMap(){
    const deleteDialog = this.dialog.open(ExpandedMapDialogComponent, {
      width: '940px',
      height: '890px',
      data: {
        title: 'Electoral Map for ' + this.district?.districtName,
        mapPath: this.district?.mapPath
       }
    });
  }

  public openProfile(): void {
    const url = `https://ecsa.sa.gov.au/electoral-districts/electoral-district-profiles/${this.district?.districtName.toLowerCase()}`;
    // const url = 'https://ecsa.sa.gov.au/electoral-districts/electoral-district-profiles?view=article&id=' + this.district?.profileId ?? '';
    window.open(url, '_blank')?.focus();
  }

  public scrollTo(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public getCountProgress(): IReportCountProgress | null {
    if (!this.district) return null;

    return {
      boothsReported: this.district.districtPollingBoothsWithCounts ?? 0,
      boothsPercent: this.district.pollingBoothCountPercent ?? 0,
      boothsTotal: this.district.districtPollingBooths ?? 0,

      // NOW POPULATED with real data
      earlyVotingCentresReported: this.district.districtEarlyVotingCentresWithCounts ?? 0,
      earlyVotingCentresPercent: this.district.earlyVotingCentreCountPercent ?? 0,
      earlyVotingCentresTotal: this.district.districtEarlyVotingCentres ?? 0,

      // NOW POPULATED with declaration type data
      declarationVotesReported: this.district.districtDeclarationTypesWithCounts ?? 0,
      declarationVotesPercent: this.district.declarationTypeCountPercent ?? 0,
      declarationVotesTotal: this.district.districtDeclarationTypes ?? 0,

      isFinal: this.isFinal
    };
  }

  public hasDistributionData(): boolean {
    if (!this.district) return false;
    // Show when we have finalDistribution (from haChange API) or legacy per-candidate distributionVotes
    if (this.district.finalDistribution?.length) return true;
    return this.district.candidates.some(candidate =>
      candidate.distributionVotes && candidate.distributionVotes.length > 0
    );
  }


  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
